import React from 'react';
import PropTypes from 'prop-types';
import { useListTableSkeletonRowCount } from '../../hooks/useListTableSkeletonRowCount';

import './TableSkeleton.scss';

export const TableSubtleFetchBar = () => (
    <div className="TableSubtleFetchBar" aria-hidden="true" />
);

/**
 * Inline flex/width rules aligned with react-table-6 column styles.
 *
 * @param {{ width?: number, maxWidth?: number, minWidth?: number, flex?: number }} col
 * @returns {import('react').CSSProperties}
 */
export function tableSkeletonColumnStyle(col = {}) {
    const { width, maxWidth, minWidth, flex: flexGrow } = col;

    if (width != null) {
        return {
            flex: '0 0 auto',
            width,
            maxWidth: maxWidth ?? width,
            minWidth: minWidth ?? width,
        };
    }

    const style = {
        flex: flexGrow != null ? `${flexGrow} 0 auto` : '1 0 auto',
    };
    if (maxWidth != null)
        style.maxWidth = maxWidth;
    if (minWidth != null)
        style.minWidth = minWidth;
    return style;
}

function skeleton_align_from_header_class(headerClassName, className) {
    const cn = `${headerClassName || ''} ${className || ''}`;
    if (cn.includes('textCenter') || cn.includes('text-center') || cn.includes('actions-cell'))
        return 'center';
    if (cn.includes('textRight') || cn.includes('text-right'))
        return 'right';
    return 'left';
}

function skeleton_sort_from_table_state(col, sortState) {
    const sort_id = col.id ?? (typeof col.accessor === 'string' ? col.accessor : null);
    const s0 = sortState?.[0];
    if (!s0 || sort_id == null || s0.id !== sort_id)
        return null;
    return s0.desc ? 'desc' : 'asc';
}

/** Build skeleton columns from react-table column defs (same width caps). */
export function skeletonColumnsFromReactTableColumns(columns, sortState) {
    return (columns || []).map((col) => {
        const sort = skeleton_sort_from_table_state(col, sortState);
        return {
            header: typeof col.Header === 'function' ? '' : col.Header,
            width: col.width,
            maxWidth: col.maxWidth,
            minWidth: col.minWidth,
            flex: col.flex,
            minRowHeight: col.minRowHeight,
            align: skeleton_align_from_header_class(col.headerClassName, col.className),
            ...(sort ? { sort } : {}),
        };
    });
}

/**
 * Mimics react-table-6 layout (headers, optional filter row, body rows) with shimmer placeholders.
 *
 * @param {Array<{
 *   header: React.ReactNode,
 *   width?: number,
 *   maxWidth?: number,
 *   minWidth?: number,
 *   flex?: number,
 *   minRowHeight?: number,
 *   align?: 'left' | 'center' | 'right',
 *   sort?: 'asc' | 'desc' | null,
 * }>} columns
 * @param {number} rowCount
 * @param {boolean} showFilterRow
 */
const thPropsForColumn = (col) => {
    const { sort, align } = col;
    const alignClass =
        align === 'right'
            ? 'KronosTh--textRight'
            : align === 'left'
            ? 'KronosTh--textLeft'
            : 'KronosTh--textCenter';
    return {
        thClass: [
            'rt-th',
            sort === 'asc' && '-sort-asc',
            sort === 'desc' && '-sort-desc',
            alignClass,
        ]
            .filter(Boolean)
            .join(' '),
        columnStyle: tableSkeletonColumnStyle(col),
    };
};

const tdAlignClass = (align) => {
    if (align === 'right')
        return 'text-right';
    if (align === 'left')
        return 'text-left';
    return 'text-center';
};

const TableSkeleton = ({
    columns,
    rowCount,
    tableId,
    sharedPageSize,
    showFilterRow,
    className,
}) => {
    const pageRowsForTable = useListTableSkeletonRowCount(tableId, { sharedPageSize });
    const effectiveRowCount = rowCount != null ? rowCount : pageRowsForTable;
    const safeColumns = columns?.length ? columns : [{ header: '' }];

    return (
        <div
            className={[
                'ReactTable -striped -highlight KronosDataTable TableSkeleton TableSkeleton-fade',
                className || '',
            ]
                .filter(Boolean)
                .join(' ')}
            role="status"
            aria-busy="true"
            aria-label="Loading"
        >
            <div className="rt-table">
                <div className="rt-thead -header">
                    <div className="rt-tr">
                        {safeColumns.map((col, i) => {
                            const { thClass, columnStyle } = thPropsForColumn(col);
                            return (
                                <div
                                    key={`h-${i}`}
                                    className={thClass}
                                    style={columnStyle}
                                >
                                    <span className="rt-resizable-header-content">
                                        {col.header != null && col.header !== '' ? col.header : '\u00a0'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {showFilterRow && (
                    <div className="rt-thead -filters">
                        <div className="rt-tr">
                            {safeColumns.map((col, i) => {
                                const { thClass, columnStyle } = thPropsForColumn(
                                    { ...col, sort: null }
                                );
                                return (
                                    <div
                                        key={`f-${i}`}
                                        className={thClass}
                                        style={columnStyle}
                                    >
                                        <div className="TableSkeleton__shimmer TableSkeleton__filterBar" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className="rt-tbody">
                    {Array.from({ length: effectiveRowCount }).map((_, rowIndex) => (
                        <div
                            key={`r-${rowIndex}`}
                            className={`rt-tr ${rowIndex % 2 ? '-odd' : '-even'}`}
                        >
                            {safeColumns.map((col, colIndex) => (
                                <div
                                    key={`c-${rowIndex}-${colIndex}`}
                                    className={['rt-td', tdAlignClass(col.align ?? 'center')].join(' ')}
                                    style={{
                                        ...tableSkeletonColumnStyle(col),
                                        minHeight: col.minRowHeight != null ? col.minRowHeight : undefined,
                                    }}
                                >
                                    <div className="TableSkeleton__shimmer" />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

TableSkeleton.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object),
    /** If omitted, `tableId` (Redux) or 10 is used. */
    rowCount: PropTypes.number,
    /** Table id (`state.tables[tableId]`) so row count matches the table page size. */
    tableId: PropTypes.string,
    /** Same meaning as `Table`’s `sharedPageSize` prop. Default: true. */
    sharedPageSize: PropTypes.bool,
    showFilterRow: PropTypes.bool,
    className: PropTypes.string,
};

TableSkeleton.defaultProps = {
    columns: [],
    rowCount: null,
    tableId: null,
    sharedPageSize: true,
    showFilterRow: true,
    className: '',
};

export default TableSkeleton;
